import { safeFrom, safeAuth, isSupabaseAvailable } from "@/lib/safe-supabase";
import { 
  ServiceResponse, 
  successResponse, 
  errorResponse, 
  handleServiceError,
  notConfiguredError
} from "@/lib/service-errors";
import { auditSettings } from "@/lib/audit-logger";
import type { LayoutPresetId } from "@/lib/layout-types";

export type SiteSettingsFeaturesOverride = {
  donation?: { enabled?: boolean };
  membership?: { enabled?: boolean };
  events?: { enabled?: boolean };
  gallery?: { enabled?: boolean };
  news?: { enabled?: boolean };
  programs?: { enabled?: boolean };
  dailyFacts?: { enabled?: boolean };
  heroImage?: boolean;
  theme?: {
    preset?: string;
    customColors?: Record<string, string>;
  };
  layout?: {
    preset?: LayoutPresetId;
  };
  navigation?: {
    showMembershipButton?: boolean;
    showDonateButton?: boolean;
    showEventsButton?: boolean;
  };
  // Allow additional dynamic properties
  [key: string]: unknown;
};

export type OrganizationDetails = {
  organizationName?: string;
  tagline?: string;
  description?: string;
  foundedYear?: number;
  logoUrl?: string;
  contact?: {
    address?: {
      street?: string;
      city?: string;
      state?: string;
      zip?: string;
      country?: string;
    };
    phone?: string;
    email?: string;
    hoursOfOperation?: string;
  };
  socialLinks?: Array<{
    platform: string;
    url: string;
    enabled: boolean;
  }>;
  hero?: {
    title?: string;
    highlightedText?: string;
    subtitle?: string;
    showStats?: boolean;
    stats?: Array<{
      value: string;
      label: string;
    }>;
  };
  legacyCTA?: {
    title?: string;
    description?: string;
  };
  welcomeSection?: {
    title?: string;
    highlightedText?: string;
    description?: string;
    buttonText?: string;
    buttonLink?: string;
    enabled?: boolean;
  };
  seo?: {
    defaultTitle?: string;
    titleTemplate?: string;
    defaultDescription?: string;
    defaultKeywords?: string[];
  };
};

export type SiteSettingsRow = {
  id: number;
  features: SiteSettingsFeaturesOverride;
  organization_details?: OrganizationDetails;
  updated_at: string;
  updated_by: string | null;
};

const SETTINGS_ROW_ID = 1;

export const siteSettingsService = {
  async getSiteSettings(): Promise<ServiceResponse<SiteSettingsRow | null>> {
    if (!isSupabaseAvailable()) {
      return successResponse(null);
    }

    try {
      const { data, error } = await safeFrom("site_settings")
        .select("id, features, organization_details, updated_at, updated_by")
        .eq("id", SETTINGS_ROW_ID)
        .maybeSingle();

      if (error) {
        // Check if the error is due to missing organization_details column
        const errorMessage = (error.message || String(error) || '').toLowerCase();
        const errorCode = (error as any).code || (error as any).status || (error as any).statusCode;
        
        if (
          errorCode === '42703' || // PostgreSQL "column does not exist"
          errorMessage.includes('organization_details does not exist') ||
          errorMessage.includes('column') && errorMessage.includes('does not exist')
        ) {
          // Column doesn't exist yet - return data without organization_details
          // This allows the app to work while migration is pending
          const { data: fallbackData, error: fallbackError } = await safeFrom("site_settings")
            .select("id, features, updated_at, updated_by")
            .eq("id", SETTINGS_ROW_ID)
            .maybeSingle();
          
          if (fallbackError) {
            throw fallbackError;
          }
          
          return successResponse(fallbackData ? { ...fallbackData, organization_details: undefined } : null);
        }
        throw error;
      }

      return successResponse(data ? (data as SiteSettingsRow) : null);
    } catch (error) {
      return errorResponse(handleServiceError(error, 'siteSettingsService.getSiteSettings'));
    }
  },

  async updateFeatures(partial: SiteSettingsFeaturesOverride): Promise<ServiceResponse<SiteSettingsRow>> {
    if (!isSupabaseAvailable()) {
      return errorResponse(notConfiguredError('Supabase'));
    }

    try {
      const { user } = await safeAuth.getUser();
      if (user?.error) {
        throw user.error;
      }

      // Get existing settings
      const existingResult = await this.getSiteSettings();
      if (!existingResult.success) {
        console.error('[siteSettingsService] Failed to get existing settings:', existingResult.error);
        throw existingResult.error;
      }

      const existingFeatures = existingResult.data?.features ?? {};
      
      console.log('[siteSettingsService] Existing features:', existingFeatures);
      console.log('[siteSettingsService] Partial update:', partial);
      
      const merged: SiteSettingsFeaturesOverride = {
        ...existingFeatures,
        ...partial,
        donation: {
          ...(existingFeatures.donation ?? {}),
          ...(partial.donation ?? {}),
        },
        membership: {
          ...(existingFeatures.membership ?? {}),
          ...(partial.membership ?? {}),
        },
        events: {
          ...(existingFeatures.events ?? {}),
          ...(partial.events ?? {}),
        },
        gallery: {
          ...(existingFeatures.gallery ?? {}),
          ...(partial.gallery ?? {}),
        },
        news: {
          ...(existingFeatures.news ?? {}),
          ...(partial.news ?? {}),
        },
        programs: {
          ...(existingFeatures.programs ?? {}),
          ...(partial.programs ?? {}),
        },
        theme: partial.theme !== undefined 
          ? { ...(existingFeatures.theme ?? {}), ...partial.theme }
          : existingFeatures.theme,
        layout: partial.layout !== undefined
          ? { ...(existingFeatures.layout ?? {}), ...partial.layout }
          : existingFeatures.layout,
        navigation: partial.navigation !== undefined
          ? { ...(existingFeatures.navigation ?? {}), ...partial.navigation }
          : existingFeatures.navigation,
      };

      console.log('[siteSettingsService] Merged features:', merged);

      const { data, error } = await safeFrom("site_settings")
        .update({
          features: merged,
          updated_by: user?.user?.id ?? null,
        })
        .eq("id", SETTINGS_ROW_ID)
        .select("id, features, organization_details, updated_at, updated_by")
        .single();

      if (error) {
        console.error('[siteSettingsService] Supabase update error:', error);
        throw error;
      }

      const updated = data as SiteSettingsRow;
      console.log('[siteSettingsService] Update successful:', updated);
      
      // Audit log settings changes
      Object.keys(partial).forEach(key => {
        const oldVal = existingFeatures[key as keyof typeof existingFeatures];
        const newVal = partial[key as keyof typeof partial];
        if (oldVal !== newVal) {
          auditSettings.change(key, oldVal, newVal);
        }
      });
      
      return successResponse(updated);
    } catch (error) {
      console.error('[siteSettingsService] Error in updateFeatures:', error);
      return errorResponse(handleServiceError(error, 'siteSettingsService.updateFeatures', { partial }));
    }
  },

  async updateOrganizationDetails(partial: Partial<OrganizationDetails>): Promise<ServiceResponse<SiteSettingsRow>> {
    if (!isSupabaseAvailable()) {
      return errorResponse(notConfiguredError('Supabase'));
    }

    try {
      const { user } = await safeAuth.getUser();
      if (user?.error) {
        throw user.error;
      }

      // Get existing settings
      const existingResult = await this.getSiteSettings();
      if (!existingResult.success) {
        console.error('[siteSettingsService] Failed to get existing settings:', existingResult.error);
        
        // Check if error is due to missing column
        const errorMessage = existingResult.error.getUserMessage().toLowerCase();
        if (errorMessage.includes('organization_details does not exist') || 
            errorMessage.includes('column') && errorMessage.includes('does not exist')) {
          return errorResponse(handleServiceError(
            new Error('The organization_details column does not exist. Please run the database migration: 20260106000000_add_organization_details.sql'),
            'siteSettingsService.updateOrganizationDetails'
          ));
        }
        
        throw existingResult.error;
      }

      const existingOrgDetails = existingResult.data?.organization_details ?? {};
      
      console.log('[siteSettingsService] Existing organization details:', existingOrgDetails);
      console.log('[siteSettingsService] Partial update:', partial);
      
      // Deep merge organization details
      const merged: OrganizationDetails = {
        ...existingOrgDetails,
        ...partial,
        contact: partial.contact !== undefined
          ? {
              ...(existingOrgDetails.contact ?? {}),
              ...partial.contact,
              address: partial.contact.address !== undefined
                ? {
                    ...(existingOrgDetails.contact?.address ?? {}),
                    ...partial.contact.address,
                  }
                : existingOrgDetails.contact?.address,
            }
          : existingOrgDetails.contact,
        socialLinks: partial.socialLinks !== undefined
          ? partial.socialLinks
          : existingOrgDetails.socialLinks,
        hero: partial.hero !== undefined
          ? {
              ...(existingOrgDetails.hero ?? {}),
              ...partial.hero,
              stats: partial.hero.stats !== undefined
                ? partial.hero.stats
                : existingOrgDetails.hero?.stats,
            }
          : existingOrgDetails.hero,
        seo: partial.seo !== undefined
          ? {
              ...(existingOrgDetails.seo ?? {}),
              ...partial.seo,
              defaultKeywords: partial.seo.defaultKeywords !== undefined
                ? partial.seo.defaultKeywords
                : existingOrgDetails.seo?.defaultKeywords,
            }
          : existingOrgDetails.seo,
        welcomeSection: partial.welcomeSection !== undefined
          ? {
              ...(existingOrgDetails.welcomeSection ?? {}),
              ...partial.welcomeSection,
            }
          : existingOrgDetails.welcomeSection,
      };

      console.log('[siteSettingsService] Merged organization details:', merged);

      const { data, error } = await safeFrom("site_settings")
        .update({
          organization_details: merged,
          updated_by: user?.user?.id ?? null,
        })
        .eq("id", SETTINGS_ROW_ID)
        .select("id, features, organization_details, updated_at, updated_by")
        .single();

      if (error) {
        console.error('[siteSettingsService] Supabase update error:', error);
        throw error;
      }

      const updated = data as SiteSettingsRow;
      console.log('[siteSettingsService] Organization details update successful:', updated);
      
      // Audit log
      auditSettings.change('organization_details', existingOrgDetails, merged);
      
      return successResponse(updated);
    } catch (error) {
      console.error('[siteSettingsService] Error in updateOrganizationDetails:', error);
      return errorResponse(handleServiceError(error, 'siteSettingsService.updateOrganizationDetails', { partial }));
    }
  },
};


